import * as THREE from 'three';
import {
	PhotonGridCell,
	PhotonWorkerOutput,
	SerializedMesh,
} from '@/workers/photon.worker';

interface ExecutePhotonWorkerPassParams {
	cells: PhotonGridCell[];
	shadowCasters: SerializedMesh[];
	sunPosition: THREE.Vector3;
	sceneCenter: THREE.Vector3;
	sceneDiskRadius: number;
	photonsPerCell: number;
	photonPowerWatts: number;
	emissionMode: 'direct' | 'diffuse';
	maxBounces: number;
	siteLimitPolygon?: { x: number; y: number }[];
	collectVisualizationData?: boolean;
	collectPhotonPaths?: boolean;
	maxPhotonPaths?: number;
	onProgress?: (passProgress: number) => void;
	onComplete: (data: PhotonWorkerOutput & { type: 'complete'; workerIndex?: number }) => void;
}

export const executePhotonWorkerPass = async ({
	cells,
	shadowCasters,
	sunPosition,
	sceneCenter,
	sceneDiskRadius,
	photonsPerCell,
	photonPowerWatts,
	emissionMode,
	maxBounces,
	siteLimitPolygon,
	collectVisualizationData,
	collectPhotonPaths,
	maxPhotonPaths,
	onProgress,
	onComplete,
}: ExecutePhotonWorkerPassParams): Promise<void> => {
	if (cells.length === 0 || photonsPerCell <= 0 || photonPowerWatts <= 0) return;

	const numCores = navigator.hardwareConcurrency || 4;
	const cpuLimitedWorkers = Math.min(Math.max(1, numCores - 1), 8);

	// Shadow casters are cloned into each worker; cap fan-out to avoid memory spikes.
	const shadowCasterBytes = shadowCasters.reduce(
		(sum, caster) => sum + caster.vertices.byteLength + caster.indices.byteLength,
		0
	);
	const workerGeometryBudgetBytes = 256 * 1024 * 1024;
	const memoryLimitedWorkers =
		shadowCasterBytes > 0
			? Math.max(1, Math.floor(workerGeometryBudgetBytes / shadowCasterBytes))
			: cpuLimitedWorkers;
	const numWorkers = Math.max(1, Math.min(cpuLimitedWorkers, memoryLimitedWorkers));
	const cellsPerWorker = Math.ceil(cells.length / numWorkers);
	const workerPromises: Promise<void>[] = [];
	let completedWorkers = 0;

	for (let workerIdx = 0; workerIdx < numWorkers; workerIdx++) {
		const startIdx = workerIdx * cellsPerWorker;
		const endIdx = Math.min(startIdx + cellsPerWorker, cells.length);
		const workerCells = cells.slice(startIdx, endIdx);

		if (workerCells.length === 0) continue;

		const worker = new Worker(new URL('../workers/photon.worker.ts', import.meta.url), {
			type: 'module',
		});

		const workerInput = {
			cells: workerCells,
			shadowCasters,
			sunPosition: { x: sunPosition.x, y: sunPosition.y, z: sunPosition.z },
			sceneCenter: { x: sceneCenter.x, y: sceneCenter.y, z: sceneCenter.z },
			sceneDiskRadius,
			photonsPerCell,
			photonPowerWatts,
			emissionMode,
			maxBounces,
			workerIndex: workerIdx,
			siteLimitPolygon,
			collectVisualizationData,
			collectPhotonPaths,
			maxPhotonPaths,
		};

		const workerPromise = new Promise<void>((resolve, reject) => {
			worker.onmessage = (e) => {
				if (e.data.type === 'progress') {
					const passProgress =
						(completedWorkers * cellsPerWorker + e.data.completed) /
						Math.max(1, cells.length);
					onProgress?.(passProgress);
				} else if (e.data.type === 'complete') {
					onComplete(e.data as PhotonWorkerOutput & { type: 'complete'; workerIndex?: number });
					completedWorkers++;
					worker.terminate();
					resolve();
				}
			};

			worker.onerror = (error) => {
				worker.terminate();
				reject(error);
			};
		});

		workerPromises.push(workerPromise);
		worker.postMessage(workerInput);
	}

	await Promise.all(workerPromises);
};
